import { useMemo, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { BrandMarkIcon } from '@xantory/icons';
import { styles } from './sidebar.styles';
import { useT } from '@xantory/i18n';
import { Search } from '../Search/search';
import { Wordmark } from '../Wordmark/wordmark';
import { NavRail, NavTree, type NavSection } from './nav-tree';
import { filterSections } from './nav-model';
export type { NavLeaf, NavNode, NavSection } from './nav-tree';
export type SidebarMode = 'full' | 'rail' | 'drawer';
interface SidebarProps {
  sections: NavSection[];
  activeKey: string;
  mode: SidebarMode;
  /** Drawer (mobile) open state — only meaningful when mode === 'drawer'. */
  drawerOpen?: boolean;
  /** Where the brand/logo links to — the app's landing page. */
  homeHref?: string | undefined;
  /** Fired on any nav click — used to close the mobile drawer. */
  onNavigate?: (() => void) | undefined;
  /** Live counts by nav key (unread notifications) — supplied by the app layer. */
  badges?: Record<string, number> | undefined;
}
export function Sidebar({
  sections,
  activeKey,
  mode,
  drawerOpen = false,
  homeHref = '/',
  onNavigate,
  badges
}: SidebarProps) {
  const t = useT('ui');
  const rail = mode === 'rail';
  const [navQuery, setNavQuery] = useState('');
  const visible = useMemo(() => filterSections(sections, navQuery), [sections, navQuery]);
  return <aside {...stylex.props(styles.sidebar, mode === 'drawer' && styles.drawer, mode === 'drawer' && drawerOpen && styles.drawerOpen)}>
      <a href={homeHref} onClick={onNavigate} aria-label="Sentinel" {...stylex.props(styles.brand, rail && styles.brandCentered)}>
        <div {...stylex.props(styles.brandMark)}>
          <BrandMarkIcon size={18} />
        </div>
        {!rail && <Wordmark sub="by Xantory" />}
      </a>

      {!rail && <div {...stylex.props(styles.navFilter)}>
          <Search tone="sunken" placeholder={t('filterNav')} value={navQuery} onInput={e => setNavQuery((e.target as HTMLInputElement).value)} />
        </div>}

      {/* Two components, not one with a `rail` flag: expanding must MOUNT the tree so
          it scrolls to the current page (see NavTree). The rail has no search box, so
          it always gets every section. The tree remounts on filter change so groups
          revealed by a match render open. */}
      {rail ? <NavRail sections={sections} activeKey={activeKey} onNavigate={onNavigate} badges={badges} /> : <NavTree key={navQuery ? `q:${navQuery}` : 'all'} sections={visible} activeKey={activeKey} onNavigate={onNavigate} badges={badges} />}
    </aside>;
}